import { memo, useMemo } from 'react';
import { renderMessage } from '@/lib/markdown';
import type { TextMessage } from '@/session/store';
import { Sparkle } from './icons/Sparkle';

/**
 * Memoized on the message text, because the open assistant bubble is rewritten on every streamed
 * chunk and Markdown-rendering the whole conversation each time is what makes a stream stutter.
 */
export const MessageBubble = memo(function MessageBubble({ message }: { message: TextMessage }) {
  const html = useMemo(
    () => (message.role === 'assistant' ? renderMessage(message.text) : ''),
    [message.role, message.text],
  );

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="bg-accent max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 text-[15px] leading-relaxed text-white shadow-lg">
          {message.text}
        </p>
      </div>
    );
  }

  // The placeholder the reply streams into. Until a word arrives the thinking indicator is what
  // stands in for it, so an empty bubble would be a second, blank one beside it.
  if (!message.text.trim()) return null;

  return (
    <div className="flex gap-3">
      <span className="border-line bg-raised text-accent-soft mt-0.5 grid size-7 shrink-0 place-items-center rounded-full border">
        <Sparkle className="size-3.5" />
      </span>
      <div className="reply min-w-0 flex-1" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
});
